import React, { useState, useEffect } from 'react';
// 🌟 FIXED: Removed 'Image', 'MessageSquare', and 'AlertCircle' since they aren't used
import { Star } from 'lucide-react';
const ProductReviewsFeed = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [metrics, setMetrics] = useState({ average: 0, total: 0, breakdown: {} });
  const [loading, setLoading] = useState(true);
  const [activeMediaModal, setActiveMediaModal] = useState(null); // Tracks clicked preview image/video

  useEffect(() => {
    const fetchProductReviews = async () => {
      try {
        const response = await fetch(`https://api.modischekledings.com/api/reviews/product/${productId}`);
        const data = await response.json();
        if (data.success) {
          setReviews(data.reviews || []);
          setMetrics(data.metrics || { average: 0, total: 0, breakdown: {} });
        }
      } catch (err) {
        console.error("Failed fetching review matrix:", err);
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchProductReviews();
  }, [productId]);

  if (loading) return <div className="py-12 text-center font-mono text-[10px] uppercase tracking-widest text-[#664229]/60">Analyzing Verified Customer Feedback...</div>;

  return (
    <div className="border-t border-[#2C1E12]/10 mt-20 pt-16 max-w-[1200px] mx-auto px-4 font-sans text-left">
      {/* SECTION HEADER */}
      <div className="mb-12">
        <h3 className="font-serif text-2xl uppercase tracking-wider text-[#2C1E12]">Customer Reviews</h3>
        <p className="text-[9px] uppercase tracking-[0.25em] text-[#664229] opacity-70 mt-1">Authentic reflections from the showroom floor</p>
      </div>

      {metrics.total === 0 ? (
        <div className="bg-[#2C1E12]/5 p-8 text-center rounded-sm border border-[#2C1E12]/5">
          <p className="text-xs font-mono uppercase text-[#664229]/60">No reviews have been written for this silhouette yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* LEFT COLUMN: ACCUMULATED SCORE & SCORE METRICS METERS */}
          <div className="lg:col-span-4 bg-[#FAF9F6] border border-[#E5D3B3]/40 p-6 sm:p-8 rounded-sm space-y-6 sticky top-36">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-serif font-black text-[#2C1E12]">{metrics.average.toFixed(1)}</span>
                <span className="text-xs font-mono text-zinc-400">/ 5.0</span>
              </div>
              <div className="flex items-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    size={14} 
                    className={`${star <= Math.round(metrics.average) ? 'text-[#664229] fill-[#664229]' : 'text-zinc-200'}`}
                  />
                ))}
                <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider pl-2">({metrics.total} Verified Purchases)</span>
              </div>
            </div>

            {/* RATINGS RATIO BREAKDOWN METERS */}
            <div className="space-y-2.5 pt-4 border-t border-zinc-100">
              {[5, 4, 3, 2, 1].map((ratingNum) => {
                const count = metrics.breakdown[ratingNum] || 0;
                const percentage = metrics.total > 0 ? (count / metrics.total) * 100 : 0;
                return (
                  <div key={ratingNum} className="flex items-center text-[10px] font-mono uppercase text-zinc-500 gap-3">
                    <span className="w-3 text-right font-bold text-[#2C1E12]">{ratingNum}★</span>
                    <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#664229] transition-all duration-500" style={{ width: `${percentage}%` }} />
                    </div>
                    <span className="w-6 text-zinc-400 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT COLUMN: ACTIVE CHRONOLOGICAL REVIEW CHANNELS STREAM */}
          <div className="lg:col-span-8 space-y-8 divide-y divide-zinc-100">
            {reviews.map((review, rIdx) => (
              <div key={review._id || rIdx} className={`w-full text-left ${rIdx > 0 ? 'pt-8' : ''}`}>
                
                {/* USER PROFILE INFO ROW */}
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          size={10} 
                          className={`${star <= review.rating ? 'text-[#664229] fill-[#664229]' : 'text-zinc-200'}`}
                        />
                      ))}
                    </div>
                    <h4 className="text-[10px] font-serif uppercase tracking-widest font-black text-[#2C1E12] mt-1.5">
                      {review.user?.name || "Verified Client"}
                    </h4>
                  </div>
                  <span className="text-[8px] font-mono text-zinc-400">
                    {new Date(review.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>

                {/* TEXT COMMENTS FEED BODY */}
                <p className="text-xs text-[#2C1E12]/80 leading-relaxed font-sans mt-3 whitespace-pre-line max-w-2xl">
                  {review.comment}
                </p>

                {/* CLIENT MEDIA ATTACHMENTS STRIP PREVIEW */}
                {review.media && review.media.length > 0 && (
                  <div className="flex items-center gap-2.5 mt-4">
                    {review.media.map((mediaItem, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => setActiveMediaModal(mediaItem)}
                        className="w-14 h-18 bg-zinc-50 border border-zinc-200 rounded-xs overflow-hidden cursor-zoom-in hover:opacity-90 transition shadow-xs flex-shrink-0"
                      >
                        {mediaItem.type?.startsWith('video/') ? (
                          <div className="w-full h-full flex flex-col items-center justify-center text-[6px] font-mono font-bold bg-zinc-900 text-[#F5F5DC] uppercase p-0.5 text-center">
                            ▶ Video
                          </div>
                        ) : (
                          <img src={mediaItem.data || mediaItem} alt="User proof" className="w-full h-full object-cover" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      )}

      {/* FULL VIEW MEDIA LIGHTBOX LAYER */}
      {activeMediaModal && (
        <div 
          onClick={() => setActiveMediaModal(null)}
          className="fixed inset-0 bg-black/90 z-[300] backdrop-blur-xs flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="max-w-3xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
            {activeMediaModal.type?.startsWith('video/') ? (
              <video src={activeMediaModal.data} controls className="max-w-full max-h-[85vh] object-contain" autoPlay />
            ) : (
              <img src={activeMediaModal.data || activeMediaModal} alt="Expanded display" className="max-w-full max-h-[85vh] object-contain rounded-sm" />
            )}
            <button 
              onClick={() => setActiveMediaModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-zinc-300 font-mono text-xs uppercase tracking-widest bg-transparent border-none cursor-pointer flex items-center gap-1"
            >
              ✕ Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviewsFeed;